import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip as MuiTooltip, Box } from '@mui/material';
import { Spacer } from '@eatclubapp/ec-component-library';
import useStyles from './PopoverTooltipStyles';

const PopoverTooltip = ({
  children,
  title,
  message,
  placement = 'top',
  anchorOrigin,
  transformOrigin,
  offsetX = 0,
  offsetY = 0,
  breakpointMdDown = false,
}) => {
  const classes = useStyles();

  const renderMessage = () => {
    if (!message) return null;

    // If message is a React node, render it directly
    if (React.isValidElement(message)) {
      return <Box className={classes.description}>{message}</Box>;
    }

    // Replace literal \n with actual newline characters for whiteSpace: 'pre-line' to work
    const processedMessage = typeof message === 'string' ? message.replace(/\\n/g, '\n') : message;

    return <Box className={classes.description}>{processedMessage}</Box>;
  };

  const tooltipContent = (
    <Spacer direction='vertical' gap={12}>
      {title && <Box className={classes.title}>{title}</Box>}
      {renderMessage()}
    </Spacer>
  );

  const tooltipProps = {
    title: tooltipContent,
    placement,
    enterTouchDelay: 0,
    enterDelay: 100,
    leaveDelay: 200,
    leaveTouchDelay: 10000, // Keep tooltip open longer on mobile/touch devices
    disableHoverListener: false,
    disableTouchListener: false,
    disableFocusListener: true,
    disableInteractive: false,
    ...(anchorOrigin && { anchorOrigin }),
    ...(transformOrigin && { transformOrigin }),
    PopperProps: {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [offsetX, offsetY],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 24,
          },
        },
      ],
      style: {
        pointerEvents: 'auto',
      },
    },
    componentsProps: {
      tooltip: {
        sx: {
          padding: '16px',
          maxWidth: '400px',
          backgroundColor: '#FFF',
          borderRadius: '24px',
          fontSize: '14px',
          boxShadow: '0 4px 4px 0 rgba(194, 194, 194, 0.25)',
          pointerEvents: 'auto',
          '& .MuiTooltip-arrow': {
            color: '#FFF',
          },
        },
      },
    },
  };

  // wrapper component to prevent prop forwarding to DOM elements and fix log warnings in console
  const TriggerWrapper = React.forwardRef((props, ref) => {
    // Filter out any props that shouldn't be on DOM elements
    const { anchorOrigin, transformOrigin, ...domProps } = props;
    return (
      <Box
        ref={ref}
        component='span'
        style={{ display: 'inline-block', width: '100%' }}
        {...domProps}
      >
        {children}
      </Box>
    );
  });

  TriggerWrapper.displayName = 'TriggerWrapper';

  return (
    <MuiTooltip {...tooltipProps}>
      <TriggerWrapper />
    </MuiTooltip>
  );
};

PopoverTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  placement: PropTypes.oneOf([
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'center', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  transformOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'center', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  breakpointMdDown: PropTypes.bool,
};

export default PopoverTooltip;
